import { isAxiosError, type AxiosError } from 'axios';

export interface ApiFieldError {
  path: string;
  message: string;
  code?: string;
}

export interface ApiErrorBody {
  message?: string;
  details?: unknown;
  [key: string]: unknown;
}

function toFieldErrors(details: unknown): ApiFieldError[] {
  if (!Array.isArray(details)) return [];
  const fields: ApiFieldError[] = [];
  for (const item of details) {
    if (typeof item !== 'object' || item === null) continue;
    const { path, message, code } = item as Record<string, unknown>;
    if (typeof message !== 'string') continue;
    fields.push({
      path: typeof path === 'string' ? path : '',
      message,
      ...(typeof code === 'string' ? { code } : {}),
    });
  }
  return fields;
}

function formatFieldErrors(fields: ApiFieldError[]): string {
  return fields.map((f) => (f.path ? `${f.path}: ${f.message}` : f.message)).join('; ');
}

/* The transport-independent half: given a status and a parsed body, what should the user read? */
export function resolveApiErrorMessage(
  status: number | undefined,
  body: unknown,
): string | undefined {
  const raw = (body as ApiErrorBody | undefined)?.message;
  const message = Array.isArray(raw) ? raw.filter((m) => typeof m === 'string').join(', ') : raw;
  const fields = toFieldErrors((body as ApiErrorBody | undefined)?.details);
  if (typeof message === 'string' && message.length > 0) {
    if (message === 'Validation failed' && fields.length > 0) {
      return `Validation failed: ${formatFieldErrors(fields)}`;
    }
    return message;
  }
  if (fields.length > 0) return `Validation failed: ${formatFieldErrors(fields)}`;

  if (status === 429) return 'Too many requests. Please wait a moment and try again.';
  return undefined;
}

function resolveAxiosErrorMessage(error: AxiosError): string | undefined {
  return resolveApiErrorMessage(error.response?.status, error.response?.data);
}

export function normalizeApiError(error: unknown): unknown {
  if (isAxiosError(error) && error.response) {
    const message = resolveAxiosErrorMessage(error);
    if (message) error.message = message;
  }
  return error;
}

/**
 * Extracts a user-facing message from an error value. Safe to call on
 * errors that already went through `normalizeApiError` (returns
 * `error.message` as-is) as well as raw Axios/unknown errors (falls back to
 * parsing `error.response.data` directly), so existing call sites don't need
 * to know which client produced the error.
 */
export function getApiErrorMessage(
  error: unknown,
  fallback = 'Something went wrong. Please try again.',
): string {
  if (isAxiosError(error)) {
    const message = resolveAxiosErrorMessage(error);
    if (message) return message;
  }
  return error instanceof Error && error.message ? error.message : fallback;
}

export function getApiFieldErrors(error: unknown): ApiFieldError[] {
  if (isAxiosError(error)) {
    return toFieldErrors((error.response?.data as ApiErrorBody | undefined)?.details);
  }
  return [];
}
